import { sparkles } from "@assets/sparkles.tsx";
import "./SmartSync.scss";
import { useState } from "react";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useExtensionCheck } from "@/utils/hooks/useExtensionCheck";
import { ActionIcon, Button, Group, Popover, Stack, Text } from "@mantine/core";
import ExtensionPopoverContent from "@/components/ExtensionPopoverContent/ExtensionPopoverContent";
import { useExtensionPopoverStore } from "@/utils/stores/useExtensionPopoverStore";
import { startSurvey } from "@/utils/intercom";
import { SURVEY_IDS } from "@/constants/intercom";
import { EXTENSION_DOWNLOAD_URL } from "@/constants";
import { openExtensionWithClient } from "@/utils/extensionClient";
import { showNotification } from "@mantine/notifications";
import { useParams } from "react-router-dom";

interface SmartSyncProps {
  name: string;
  copyButton?: boolean;
  responsive?: boolean;
  onClick?: () => void;
  newProfile?: boolean;
  taxYear?: number;
}

const SmartSync: React.FC<SmartSyncProps> = ({
  name,
  copyButton,
  responsive,
  onClick,
  taxYear,
}) => {
  const { userId } = useParams();
  const { openedPopovers, setPopoverState } = useExtensionPopoverStore();

  const [actionComplete, setActionComplete] = useState(false);
  const [warningPopoverOpened, setWarningPopoverOpened] = useState(false);

  const isExtensionInstalled = useExtensionCheck();

  /**
   * Opens the extension with the current client and optional tax year
   */
  const openExtensionWithCurrentClient = async () => {
    if (!userId) {
      console.error("No user ID available");
      showNotification({
        title: "Error",
        message: "Client ID not found",
        color: "red",
      });
      return;
    }

    try {
      const success = await openExtensionWithClient(userId, taxYear);

      if (success) {
        setActionComplete(true);
        setTimeout(() => setActionComplete(false), 3000);
      } else {
        showNotification({
          title: "Warning",
          message:
            "Extension may not have opened properly. Please try manually.",
          color: "yellow",
        });
      }
    } catch (error) {
      console.error("Failed to open extension with client:", error);
      showNotification({
        title: "Error",
        message: "Failed to open extension. Please try again.",
        color: "red",
      });
    }
  };

  const handleClick = async (): Promise<void> => {
    // Trigger Intercom survey when SmartSync button is clicked
    startSurvey(SURVEY_IDS.SMART_SYNC);

    if (copyButton) {
      await openExtensionWithCurrentClient();
    }

    onClick?.();
  };

  const buttonElement = responsive ? (
    <span style={{ display: "inline-block" }}>
      <button
        className={`responsive-sync-btn ${actionComplete ? "copied" : ""}`}
        onClick={handleClick}
      />
    </span>
  ) : (
    <button
      className={`ai-button ${actionComplete ? "copied" : ""}`}
      onClick={handleClick}
    >
      {actionComplete ? <IconCheck size={18} /> : sparkles}
    </button>
  );

  // Warning icon with installation instructions popover
  const warningIcon = !isExtensionInstalled && (
    <Popover
      opened={warningPopoverOpened}
      onChange={setWarningPopoverOpened}
      position="bottom"
      withArrow
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          color="yellow"
          size="sm"
          onClick={() => setWarningPopoverOpened(!warningPopoverOpened)}
        >
          <IconAlertCircle size={18} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="md" style={{ maxWidth: 300 }}>
          <Text size="sm" fw={600}>
            Extension Not Detected
          </Text>
          <Text size="sm" c="dimmed">
            The SmartWiz Chrome Extension is required for the best experience.
            Install it to automatically open and load client data.
          </Text>
          <Button
            component="a"
            href={EXTENSION_DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            fullWidth
          >
            Download Extension
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );

  return (
    <Group
      gap={4}
      className={`${
        responsive ? "responsive-smart-sync" : "smart-sync-container"
      }`}
      align="center"
    >
      {warningIcon}
      <Popover
        opened={openedPopovers[name] ?? false}
        onChange={(opened) => setPopoverState(name, opened)}
        shadow="md"
        position="bottom-end"
        withinPortal
      >
        <Popover.Target>{buttonElement}</Popover.Target>
        <Popover.Dropdown className="extension-popover-dropdown">
          <ExtensionPopoverContent
            onClose={() => setPopoverState(name, false)}
          />
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};

export default SmartSync;
